// src/pages/sm/AllowancePolicyManager.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, FormControlLabel, Switch, Grid, Stack, Tooltip,
    Chip, Divider, Paper, Fade, Tab, Tabs, MenuItem, Select, FormControl, InputLabel, Autocomplete
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Policy as PolicyIcon,
    PersonOff as ExclusionIcon,
    SettingsSuggest as MapIcon,
    AccountTree as OrgIcon,
    Stars as GradeIcon,
    Work as JobIcon,
    Store as BranchIcon,
    Save as SaveIcon,
    Close as CloseIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';

const parsePolicyRulesConfig = (rulesConfig) => {
    if (!rulesConfig) return {};
    if (typeof rulesConfig === 'object') return rulesConfig;
    try {
        return JSON.parse(rulesConfig);
    } catch (e) {
        return {};
    }
};

const getOtCalculationMode = (policy) => {
    const rules = parsePolicyRulesConfig(policy?.rules_config);
    return rules.ot_calculation_mode || 'FIXED';
};

const AllowancePolicyManager = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const [currentTab, setCurrentTab] = useState(0);
    const [policies, setPolicies] = useState([]);
    const [exclusions, setExclusions] = useState([]);
    const [elementMap, setElementMap] = useState([]);
    const [loading, setLoading] = useState(false);

    // Lookups for dropdowns
    const [orgNodes, setOrgNodes] = useState([]);
    const [grades, setGrades] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [branches, setBranches] = useState([]);
    const [elements, setElements] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState('policy'); // 'policy', 'exclusion', 'map'
    const [editingItem, setEditingItem] = useState(null);

    const [formData, setFormData] = useState({});

    const tenant = React.useMemo(() => localStorage.getItem('tenant'), []);
    const API_URL = process.env.REACT_APP_API_URL1;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const config = { withCredentials: true, headers: { 'X-Tenant': tenant } };
            const [pRes, eRes, mRes, nodeRes, gradeRes, jobRes, branchRes, elemRes, staffRes, leavesRes] = await Promise.all([
                axios.get(`${API_URL}/sm/allowance-policies`, config),
                axios.get(`${API_URL}/sm/benefit-exclusions`, config),
                axios.get(`${API_URL}/sm/allowance-element-map`, config),
                axios.get(`${API_URL}/som/nodes`, config),
                axios.get(`${API_URL}/som/grades`, config),
                axios.get(`${API_URL}/som/jobs`, config),
                axios.get(`${API_URL}/som/branches`, config),
                axios.get(`${API_URL}/sm/payroll/elements`, config),
                axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff`, config),
                axios.get(`${API_URL}/leave/types`, config)
            ]);

            if (pRes.data.success) setPolicies(pRes.data.data);
            if (eRes.data.success) setExclusions(eRes.data.data);
            if (mRes.data.success) setElementMap(mRes.data.data);
            
            if (nodeRes.data.success) setOrgNodes(buildHierarchicalList(nodeRes.data.data));
            if (gradeRes.data.success) setGrades(gradeRes.data.data);
            if (jobRes.data.success) setJobs(jobRes.data.data);
            if (branchRes.data.success) setBranches(branchRes.data.data);
            if (elemRes.data.success) setElements(elemRes.data.data);
            if (leavesRes.data.success) setLeaveTypes(leavesRes.data.data);
            
            // Staff list comes as a direct array from the controller
            const staffData = Array.isArray(staffRes.data) ? staffRes.data : (staffRes.data?.rows || []);
            setStaffList(staffData);

        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    }, [tenant, API_URL]);


    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleOpenDialog = (type, item = null) => {
        setDialogType(type);
        setEditingItem(item);
        if (type === 'policy') {
            let rules_config = item?.rules_config;
            if (typeof rules_config === 'string') {
                try { rules_config = JSON.parse(rules_config); } catch (e) { rules_config = {}; }
            }
            setFormData(item ? { ...item, rules_config: rules_config || {} } : {
                policy_name: '',
                allowance_type: 'TRANSPORT',
                org_node_id: '',
                grade_id: '',
                job_id: '',
                branch_id: '',
                amount: 0,
                min_hours: 0,
                max_rank_level: '',
                priority: 10,
                is_active: 1,
                effective_from: new Date().toISOString().split('T')[0],
                effective_to: '',
                rules_config: {}
            });
        } else if (type === 'exclusion') {
            setFormData(item || {
                staff_id: '',
                benefit_type: 'TRANSPORT',
                reason: 'HAS_FERRY',
                notes: '',
                effective_from: new Date().toISOString().split('T')[0],
                effective_to: ''
            });
        } else if (type === 'map') {
            setFormData(item || {
                element_id: '',
                allowance_type: 'TRANSPORT'
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({});
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleSubmit = async () => {
        try {
            let endpoint = '';
            let payload = { ...formData };
            if (dialogType === 'policy') {
                endpoint = 'allowance-policies';
                if (payload.rules_config) {
                    payload.rules_config = JSON.stringify(payload.rules_config);
                }
            }
            else if (dialogType === 'exclusion') endpoint = 'benefit-exclusions';
            else if (dialogType === 'map') endpoint = 'allowance-element-map';

            const url = `${API_URL}/sm/${endpoint}${editingItem ? `/${editingItem.id}` : ''}`;
            const method = (editingItem && dialogType !== 'map') ? 'put' : 'post';

            const res = await axios[method](url, payload, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });

            if (res.data.success) {
                fetchData();
                handleCloseDialog();
            }
        } catch (err) {
            console.error("Error saving:", err);
            alert(err.response?.data?.message || "Error saving data");
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm("Are you sure you want to delete this record?")) return;
        try {
            let endpoint = '';
            if (type === 'policy') endpoint = 'allowance-policies';
            else if (type === 'exclusion') endpoint = 'benefit-exclusions';
            else if (type === 'map') endpoint = 'allowance-element-map';

            const res = await axios.delete(`${API_URL}/sm/${endpoint}/${id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': tenant }
            });
            if (res.data.success) {
                fetchData();
            }
        } catch (err) {
            console.error("Error deleting:", err);
        }
    };

    const policyColumns = useMemo(() => [
        { headerName: "Policy Name", field: "policy_name", flex: 1, minWidth: 200, cellStyle: { fontWeight: 600 } },
        { 
            headerName: "Type", 
            field: "allowance_type", 
            width: 130,
            cellRenderer: p => {
                const colorMap = { TRANSPORT: 'primary', MEAL: 'success', OT: 'secondary', EARLY_ARRIVAL: 'warning', PERFECT_ATTENDANCE: 'info' };
                return <Chip label={p.value} size="small" color={colorMap[p.value] || 'default'} />;
            }
        },
        { 
            headerName: "Amount", 
            field: "amount", 
            width: 150,
            cellRenderer: p => {
                const isHourlyOt = p.data?.allowance_type === 'OT' && getOtCalculationMode(p.data) === 'PER_OT_HOUR';
                return <strong>{p.value?.toLocaleString()} MMK{isHourlyOt ? '/hr' : ''}</strong>;
            }
        },
        { 
            headerName: "Criteria", 
            flex: 1,
            cellRenderer: p => {
                const parts = [];
                if (p.data.org_node_name) parts.push(`Node: ${p.data.org_node_name}`);
                if (p.data.grade_name) parts.push(`Grade: ${p.data.grade_name}`);
                if (p.data.job_title) parts.push(`Job: ${p.data.job_title}`);
                if (p.data.branch_name) parts.push(`Branch: ${p.data.branch_name}`);
                return parts.length ? parts.join(' | ') : <span style={{ color: '#94a3b8' }}>Default (Global)</span>;
            }
        },
        { headerName: "Priority", field: "priority", width: 100 },
        {
            headerName: "Actions",
            width: 110,
            pinned: 'right',
            cellRenderer: p => (
                <Stack direction="row" spacing={0.5} sx={{ height: '100%', alignItems: 'center' }}>
                    <IconButton size="small" onClick={() => handleOpenDialog('policy', p.data)} color="primary"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete('policy', p.data.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
            )
        }
    ], []);

    const exclusionColumns = useMemo(() => [
        { headerName: "Staff", field: "full_name", flex: 1, cellStyle: { fontWeight: 600 } },
        { headerName: "Code", field: "staff_code", width: 120 },
        { 
            headerName: "Type", 
            field: "benefit_type", 
            width: 130,
            cellRenderer: p => <Chip label={p.value} size="small" variant="outlined" />
        },
        { headerName: "Reason", field: "reason", width: 150 },
        { headerName: "Notes", field: "notes", flex: 1 },
        {
            headerName: "Actions",
            width: 110,
            pinned: 'right',
            cellRenderer: p => (
                <Stack direction="row" spacing={0.5} sx={{ height: '100%', alignItems: 'center' }}>
                    <IconButton size="small" onClick={() => handleOpenDialog('exclusion', p.data)} color="primary"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete('exclusion', p.data.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </Stack>
            )
        }
    ], []);

    const mapColumns = useMemo(() => [
        { headerName: "Payroll Element", field: "element_name", flex: 1, cellStyle: { fontWeight: 600 } },
        { headerName: "Code", field: "element_code", width: 120 },
        { 
            headerName: "Blocks Allowance", 
            field: "allowance_type", 
            flex: 1,
            cellRenderer: p => <Chip label={p.value} size="small" color="error" />
        },
        {
            headerName: "Actions",
            width: 80,
            pinned: 'right',
            cellRenderer: p => (
                <IconButton size="small" onClick={() => handleDelete('map', p.data.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
            )
        }
    ], []);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppHeader onLogout={() => navigate('/login')} />
            
            <Box sx={{ p: 4, maxWidth: 1600, mx: 'auto', width: '100%', flexGrow: 1 }}>
                <Fade in timeout={500}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>
                                    Allowance Policy Engine
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                    Manage eligibility rules, org-based rates, and benefit exclusions
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog(currentTab === 0 ? 'policy' : currentTab === 1 ? 'exclusion' : 'map')}
                                sx={{ bgcolor: 'primary.main', borderRadius: 2.5, px: 3, py: 1.2, fontWeight: 700, textTransform: 'none' }}
                            >
                                {currentTab === 0 ? 'Add Policy' : currentTab === 1 ? 'Add Exclusion' : 'Map Element'}
                            </Button>
                        </Stack>

                        <Paper sx={{ borderRadius: 4, mb: 3, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: '#fff' }}>
                                <Tab icon={<PolicyIcon />} iconPosition="start" label="Rate Policies" />
                                <Tab icon={<ExclusionIcon />} iconPosition="start" label="Physical Exclusions" />
                                <Tab icon={<MapIcon />} iconPosition="start" label="Payroll Auto-Detection" />
                            </Tabs>

                            <Box sx={{ p: 0 }}>
                                <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 350px)', width: '100%' }}>
                                    {currentTab === 0 && <AgGridReact rowData={policies} columnDefs={policyColumns} defaultColDef={{ sortable: true, filter: true, resizable: true }} pagination={true} paginationPageSize={20} />}
                                    {currentTab === 1 && <AgGridReact rowData={exclusions} columnDefs={exclusionColumns} defaultColDef={{ sortable: true, filter: true, resizable: true }} pagination={true} paginationPageSize={20} />}
                                    {currentTab === 2 && <AgGridReact rowData={elementMap} columnDefs={mapColumns} defaultColDef={{ sortable: true, filter: true, resizable: true }} pagination={true} paginationPageSize={20} />}
                                </div>
                            </Box>
                        </Paper>
                    </Box>
                </Fade>
            </Box>

            {/* UNIVERSAL DIALOG */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {editingItem ? 'Edit' : 'Create New'} {dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {dialogType === 'policy' && (
                            <>
                                <TextField fullWidth label="Policy Name" name="policy_name" value={formData.policy_name || ''} onChange={handleChange} />
                                <FormControl fullWidth>
                                    <InputLabel>Allowance Type</InputLabel>
                                    <Select name="allowance_type" value={formData.allowance_type || 'TRANSPORT'} onChange={handleChange} label="Allowance Type">
                                        <MenuItem value="TRANSPORT">TRANSPORT</MenuItem>
                                        <MenuItem value="MEAL">MEAL</MenuItem>
                                        <MenuItem value="SPECIAL">Special</MenuItem>
                                        <MenuItem value="OT">OT Policy</MenuItem>
                                        <MenuItem value="EARLY_ARRIVAL">Early Arrival Incentive</MenuItem>
                                        <MenuItem value="PERFECT_ATTENDANCE">Perfect Attendance Incentive</MenuItem>
                                    </Select>
                                </FormControl>
                                <Divider><Chip label="Target Criteria (Optional)" size="small" /></Divider>
                                <Stack spacing={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Org Node</InputLabel>
                                        <Select name="org_node_id" value={formData.org_node_id || ''} onChange={handleChange} label="Org Node">
                                            <MenuItem value="">(None)</MenuItem>
                                            {orgNodes.map(n => (
                                                <MenuItem key={n.id} value={n.id} sx={{ pl: 2 + (n._depth || 0) * 2 }}>
                                                    {n._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                                    {n.node_name}{n.level_type ? ` (${n.level_type})` : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Stack direction="row" spacing={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Branch</InputLabel>
                                            <Select name="branch_id" value={formData.branch_id || ''} onChange={handleChange} label="Branch">
                                                <MenuItem value="">(None)</MenuItem>
                                                {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Grade</InputLabel>
                                            <Select name="grade_id" value={formData.grade_id || ''} onChange={handleChange} label="Grade">
                                                <MenuItem value="">(None)</MenuItem>
                                                {grades.map(g => <MenuItem key={g.id} value={g.id}>{g.grade_name}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                </Stack>

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label={formData.allowance_type === 'OT' && formData.rules_config?.ot_calculation_mode === 'PER_OT_HOUR' ? 'Rate per OT Hour (MMK)' : 'Amount (MMK)'}
                                            type="number"
                                            name="amount"
                                            value={formData.amount || 0}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    {formData.allowance_type !== 'PERFECT_ATTENDANCE' && (
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label={formData.allowance_type === 'EARLY_ARRIVAL' ? 'Min Early Arrival (Mins)' : formData.allowance_type === 'OT' ? 'Min OT Hours' : 'Min Hours'}
                                                helperText={formData.allowance_type === 'EARLY_ARRIVAL' ? 'Staff must arrive at least this many minutes early' : formData.allowance_type === 'OT' ? 'Payable OT hours needed to qualify' : 'Min shift hours to qualify'}
                                                type="number"
                                                name="min_hours"
                                                value={formData.min_hours || 0}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                    )}
                                </Grid>

                                {formData.allowance_type === 'OT' && (
                                    <FormControl fullWidth>
                                        <InputLabel>OT Allowance Calculation</InputLabel>
                                        <Select
                                            value={formData.rules_config?.ot_calculation_mode || 'FIXED'}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                rules_config: {
                                                    ...formData.rules_config,
                                                    ot_calculation_mode: e.target.value
                                                }
                                            })}
                                            label="OT Allowance Calculation"
                                        >
                                            <MenuItem value="FIXED">Fixed amount when qualified</MenuItem>
                                            <MenuItem value="PER_OT_HOUR">OT hours x rate</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                                
                                {formData.allowance_type === 'PERFECT_ATTENDANCE' && (
                                    <>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={!!formData.rules_config?.allow_late_checkin}
                                                            onChange={(e) => setFormData({ ...formData, rules_config: { ...formData.rules_config, allow_late_checkin: e.target.checked } })}
                                                        />
                                                    }
                                                    label="Allow Late Check-ins"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={!!formData.rules_config?.allow_early_checkout}
                                                            onChange={(e) => setFormData({ ...formData, rules_config: { ...formData.rules_config, allow_early_checkout: e.target.checked } })}
                                                        />
                                                    }
                                                    label="Allow Early Check-outs"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    multiple
                                                    options={leaveTypes}
                                                    getOptionLabel={(option) => option.type_name}
                                                    value={leaveTypes.filter(lt => formData.rules_config?.allowed_leave_types?.includes(lt.id))}
                                                    onChange={(e, val) => setFormData({ ...formData, rules_config: { ...formData.rules_config, allowed_leave_types: val.map(v => v.id) } })}
                                                    renderInput={(params) => <TextField {...params} label="Allowed Paid Leave Types" placeholder="Select leave types" helperText="Employees taking these leaves will NOT lose the perfect attendance incentive." />}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}

                                {formData.allowance_type === 'EARLY_ARRIVAL' && (
                                    <TextField
                                        fullWidth
                                        label="Max Rank Level (Grade Cutoff)"
                                        helperText="Staff whose grade rank_level is ≤ this value qualify. Leave blank for no restriction (all ranks)."
                                        type="number"
                                        name="max_rank_level"
                                        value={formData.max_rank_level ?? ''}
                                        onChange={handleChange}
                                        inputProps={{ min: 1 }}
                                    />
                                )}
                                <Grid container spacing={2}>
                                    <Grid item xs={6}><TextField fullWidth label="Priority (1-99)" type="number" name="priority" value={formData.priority || 10} onChange={handleChange} helperText="Lower = Higher Precedence" /></Grid>
                                    <Grid item xs={6}><FormControlLabel control={<Switch checked={formData.is_active === 1} name="is_active" onChange={handleChange} />} label="Active" /></Grid>
                                </Grid>
                            </>
                        )}

                        {dialogType === 'exclusion' && (
                            <>
                                <Autocomplete
                                    options={staffList}
                                    getOptionLabel={(option) => `${option.staff_code} - ${option.full_name}`}
                                    value={staffList.find(s => (s.staff_id || s.id) === formData.staff_id) || null}
                                    onChange={(event, newValue) => {
                                        setFormData(prev => ({ ...prev, staff_id: newValue ? (newValue.staff_id || newValue.id) : '' }));
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Select Staff" fullWidth />}
                                />
                                <FormControl fullWidth>
                                    <InputLabel>Benefit Type</InputLabel>
                                    <Select name="benefit_type" value={formData.benefit_type || 'TRANSPORT'} onChange={handleChange} label="Benefit Type">
                                        <MenuItem value="TRANSPORT">TRANSPORT</MenuItem>
                                        <MenuItem value="MEAL">MEAL</MenuItem>
                                        <MenuItem value="EARLY_ARRIVAL">EARLY_ARRIVAL</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel>Reason</InputLabel>
                                    <Select name="reason" value={formData.reason || 'HAS_FERRY'} onChange={handleChange} label="Reason">
                                        <MenuItem value="HAS_FERRY">HAS FERRY</MenuItem>
                                        <MenuItem value="HAS_CATERING">HAS CATERING</MenuItem>
                                        <MenuItem value="OTHER">OTHER</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField fullWidth label="Notes" name="notes" multiline rows={2} value={formData.notes || ''} onChange={handleChange} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}><TextField fullWidth label="From" type="date" name="effective_from" value={formData.effective_from || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                                    <Grid item xs={6}><TextField fullWidth label="To" type="date" name="effective_to" value={formData.effective_to || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                                </Grid>
                            </>
                        )}

                        {dialogType === 'map' && (
                            <>
                                <FormControl fullWidth>
                                    <InputLabel>Payroll Element</InputLabel>
                                    <Select name="element_id" value={formData.element_id || ''} onChange={handleChange} label="Payroll Element">
                                        {elements.map(e => <MenuItem key={e.id} value={e.id}>{e.element_code} - {e.element_name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth>
                                    <InputLabel>Blocks Allowance</InputLabel>
                                    <Select name="allowance_type" value={formData.allowance_type || 'TRANSPORT'} onChange={handleChange} label="Blocks Allowance">
                                        <MenuItem value="TRANSPORT">TRANSPORT</MenuItem>
                                        <MenuItem value="MEAL">MEAL</MenuItem>
                                        <MenuItem value="SPECIAL">SPECIAL</MenuItem>
                                        <MenuItem value="OT">OT</MenuItem>
                                        <MenuItem value="EARLY_ARRIVAL">EARLY_ARRIVAL</MenuItem>
                                        <MenuItem value="PERFECT_ATTENDANCE">PERFECT_ATTENDANCE</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: 'primary.main' }} startIcon={<SaveIcon />}>Save Changes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AllowancePolicyManager;
